import { create } from 'zustand';

/**
 * One-shot navigation intent from notification bell (browser URL stays clean).
 * @typedef {{ pageKey: string, entityId: string, category?: string, verb?: string, i18nKey?: string, createdAt: number }} PendingHighlight
 */

/**
 * Detail page tab highlight intent (linked rows on a detail tab table).
 * @typedef {{
 *   pageKey: string,
 *   entityId: string,
 *   tab?: string,
 *   linkedEntityIds: string[],
 *   verb?: string,
 *   i18nKey?: string,
 *   createdAt: number,
 * }} PendingDetailHighlight
 */

export const useNotificationHighlightStore = create((set, get) => ({
  pending: null,
  pendingDetail: null,

  setPendingHighlight: (payload) => {
    if (!payload?.pageKey || !payload?.entityId) return;
    set({
      pending: {
        pageKey: String(payload.pageKey),
        entityId: String(payload.entityId),
        category: payload.category ? String(payload.category) : undefined,
        verb: payload.verb ? String(payload.verb) : undefined,
        i18nKey: payload.i18nKey ? String(payload.i18nKey) : undefined,
        createdAt: Date.now(),
      },
    });
  },

  setPendingDetailHighlight: (payload) => {
    if (!payload?.pageKey || !payload?.entityId) return;
    const linked = (payload.linkedEntityIds || [])
      .map((id) => String(id))
      .filter(Boolean);
    set({
      pendingDetail: {
        pageKey: String(payload.pageKey),
        entityId: String(payload.entityId),
        tab: payload.tab ? String(payload.tab) : undefined,
        linkedEntityIds: linked,
        verb: payload.verb ? String(payload.verb) : undefined,
        i18nKey: payload.i18nKey ? String(payload.i18nKey) : undefined,
        createdAt: Date.now(),
      },
    });
  },

  /** Read intent without clearing (survives Strict Mode effect re-run). */
  peekPendingHighlight: (pageKey) => {
    const { pending } = get();
    if (!pending || pending.pageKey !== pageKey) return null;
    return pending;
  },

  peekPendingDetailHighlight: (pageKey, entityId, tab) => {
    const { pendingDetail } = get();
    if (!pendingDetail || pendingDetail.pageKey !== pageKey) return null;
    if (String(pendingDetail.entityId) !== String(entityId)) return null;
    if (tab != null && pendingDetail.tab != null && pendingDetail.tab !== tab) return null;
    return pendingDetail;
  },

  /** Clear intent after highlight applied or not-found toast (avoids effect loops). */
  acknowledgePendingHighlight: (pageKey, createdAt) => {
    const { pending } = get();
    if (!pending || pending.pageKey !== pageKey) return false;
    if (createdAt != null && pending.createdAt !== createdAt) return false;
    set({ pending: null });
    return true;
  },

  acknowledgePendingDetailHighlight: (pageKey, createdAt) => {
    const { pendingDetail } = get();
    if (!pendingDetail || pendingDetail.pageKey !== pageKey) return false;
    if (createdAt != null && pendingDetail.createdAt !== createdAt) return false;
    set({ pendingDetail: null });
    return true;
  },

  /** @deprecated Use acknowledgePendingHighlight after resolve completes. */
  consumePendingHighlight: (pageKey) => {
    const { pending } = get();
    if (!pending || pending.pageKey !== pageKey) return null;
    set({ pending: null });
    return pending;
  },

  clearPendingHighlight: () => set({ pending: null }),
  clearPendingDetailHighlight: () => set({ pendingDetail: null }),
}));
